import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "../lib/api";
import { ymd } from "../lib/date";
import { addDays } from "date-fns";
import { useEffectiveUser } from "../lib/auth";
import { can, LEVEL_LABEL, isPlatformDeveloper } from "../lib/permissions";
import { FixCoverageModal } from "../components/FixCoverageModal";
import { applyTacOverlay, isShiftActiveOn } from "../lib/tacMode";
import { readableTextOn } from "../lib/colors";

function Kpi({ label, value, hint }: { label: string; value: string | number; hint?: string }) {
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-4">
      <div className="text-xs text-slate-500">{label}</div>
      <div className="text-2xl font-semibold text-slate-900 mt-1">{value}</div>
      {hint && <div className="text-xs text-slate-400 mt-1">{hint}</div>}
    </div>
  );
}

type UnderstaffedRange = "7d" | "2w" | "month";

const DEPT_STORAGE_KEY = "dashboard.deptId";

export function Dashboard() {
  const me = useEffectiveUser();
  const mayFix = can.editRoster(me);
  const today = ymd(new Date());
  const twoWeeksOut = ymd(addDays(new Date(), 14));
  const [fixCoverage, setFixCoverage] = useState<{ shiftId: string; date: string } | null>(null);
  const [understaffedRange, setUnderstaffedRange] = useState<UnderstaffedRange>("7d");
  const rangeEnd = useMemo(() => {
    const days = understaffedRange === "7d" ? 7 : understaffedRange === "2w" ? 14 : 30;
    return ymd(addDays(new Date(), days));
  }, [understaffedRange]);

  // Dashboard is department-scoped — Patrol and Dispatch each want
  // their own KPI / coverage view. The selector persists in
  // localStorage so a supervisor lands back on whatever they were
  // last looking at; if that dept is gone we fall through to the
  // user's home dept, then to the first dept overall.
  const agency = useQuery({ queryKey: ["agency"], queryFn: api.getAgency });
  const departments = useQuery({ queryKey: ["departments"], queryFn: api.listDepartments });
  const allEmployees = useQuery({ queryKey: ["employees"], queryFn: () => api.listEmployees() });
  const myEmpDeptId = useMemo(
    () => (allEmployees.data ?? []).find((e) => e.id === me?.employeeId)?.departmentId ?? null,
    [allEmployees.data, me?.employeeId],
  );
  const [activeDeptId, setActiveDeptId] = useState<string | null>(() => {
    try { return localStorage.getItem(DEPT_STORAGE_KEY); } catch { return null; }
  });
  const resolvedDeptId = useMemo(() => {
    const list = departments.data ?? [];
    if (list.length === 0) return null;
    if (activeDeptId && list.some((d) => d.id === activeDeptId)) return activeDeptId;
    if (myEmpDeptId && list.some((d) => d.id === myEmpDeptId)) return myEmpDeptId;
    return list[0].id;
  }, [departments.data, activeDeptId, myEmpDeptId]);
  useEffect(() => {
    if (resolvedDeptId && resolvedDeptId !== activeDeptId) {
      try { localStorage.setItem(DEPT_STORAGE_KEY, resolvedDeptId); } catch { /* ignore */ }
      setActiveDeptId(resolvedDeptId);
    }
  }, [resolvedDeptId, activeDeptId]);
  const activeDept = useMemo(
    () => (departments.data ?? []).find((d) => d.id === resolvedDeptId) ?? null,
    [departments.data, resolvedDeptId],
  );
  const deptOpts = resolvedDeptId ? { departmentId: resolvedDeptId } : undefined;

  const employees = useQuery({
    queryKey: ["employees", resolvedDeptId],
    queryFn: () => api.listEmployees(deptOpts),
    enabled: resolvedDeptId !== null,
  });
  const shifts = useQuery({
    queryKey: ["shifts", resolvedDeptId],
    queryFn: () => api.listShifts(deptOpts),
    enabled: resolvedDeptId !== null,
  });
  const shiftCodes = useQuery({ queryKey: ["shiftCodes"], queryFn: () => api.listShiftCodes() });
  const todayAssignments = useQuery({
    queryKey: ["assignments", today, today],
    queryFn: () => api.listAssignments(today, today),
  });
  const myAssignments = useQuery({
    queryKey: ["assignments", today, twoWeeksOut],
    queryFn: () => api.listAssignments(today, twoWeeksOut),
    enabled: !!me?.employeeId,
  });
  const myTimeOff = useQuery({
    queryKey: ["my-timeoff", today, twoWeeksOut, me?.employeeId],
    queryFn: () => api.listTimeOff(today, twoWeeksOut),
    enabled: !!me?.employeeId,
  });
  const weekCoverage = useQuery({
    queryKey: ["coverage", resolvedDeptId, today, rangeEnd],
    queryFn: () => api.coverage(today, rangeEnd, deptOpts),
    enabled: resolvedDeptId !== null,
  });
  const pendingTimeOff = useQuery({
    queryKey: ["timeoff-pending", rangeEnd],
    queryFn: () => api.listTimeOff(today, rangeEnd),
  });
  const audit = useQuery({ queryKey: ["audit", 10], queryFn: () => api.listAudit({ limit: 10 }) });
  const users = useQuery({ queryKey: ["users"], queryFn: () => api.listUsers() });

  // userId → human-readable label, same rule as the Audit page —
  // prefer the linked employee's real name, fall back to the user's
  // login username with level label, fall back finally to a short UID.
  const userLabel = useMemo(() => {
    const byId = new Map<string, string>();
    // Use the agency-wide employees list so a reviewer from another
    // department still resolves to their name in the recent-activity
    // feed (the dashboard's main `employees` query is dept-scoped).
    const empById = new Map((allEmployees.data ?? []).map((e) => [e.id, e]));
    for (const u of users.data ?? []) {
      // The platform developer renders as "Developer" only — no
      // leading username. Every other account, including agency
      // Owners, uses their employee name (or username + level
      // fallback) just like the Audit log does.
      if (isPlatformDeveloper(u)) {
        byId.set(u.id, "Developer");
        continue;
      }
      const emp = u.employeeId ? empById.get(u.employeeId) : undefined;
      if (emp) byId.set(u.id, `${emp.firstName} ${emp.lastName}`.trim() || u.username);
      else byId.set(u.id, `${u.username} (${LEVEL_LABEL[u.level]})`);
    }
    return byId;
  }, [users.data, allEmployees.data]);

  // Tac-mode overlay: filter the dept's shifts to whatever should
  // actually be visible in the current tac state (regular+exempt
  // when off; tac+exempt when on). Exempt shifts (Dispatch, Animal
  // Control, …) stay visible regardless. Every downstream calc —
  // KPI counts, coverage shortages, today's roster — keys off
  // visibleShifts so a flipped tac mode immediately reshapes the
  // dashboard without touching the data layer.
  const visibleShifts = useMemo(
    () => applyTacOverlay(agency.data, shifts.data ?? [], employees.data ?? []).visibleShifts,
    [agency.data, shifts.data, employees.data],
  );
  const tacModeActive = agency.data?.tacModeActive === true;
  const visibleShiftIds = useMemo(
    () => new Set(visibleShifts.map((s) => s.id)),
    [visibleShifts],
  );

  // Specialty shifts that aren't part of the 24/7 rotation (e.g.
  // Traffic, SRO) don't raise coverage alarms — they aren't expected
  // to meet min staffing on any given day.
  const rotationShiftIds = useMemo(
    () => new Set(
      visibleShifts.filter((s) => s.inRotation !== false).map((s) => s.id),
    ),
    [visibleShifts],
  );
  // Per-shift weekday off-days (School Resource on weekends, etc.)
  // don't count toward the shortage list — the shift isn't scheduled
  // to run that day so 0/N isn't actually a gap.
  const shiftById = useMemo(
    () => new Map(visibleShifts.map((s) => [s.id, s])),
    [visibleShifts],
  );
  const understaffed = (weekCoverage.data ?? []).filter((c) => {
    if (!rotationShiftIds.has(c.shiftId)) return false;
    const sh = shiftById.get(c.shiftId);
    // Shift not in rotation on this date — tac-transition mismatch
    // (regular on a tac day or vice versa) or the per-shift weekday
    // opt-out — drops the row. Otherwise a Nights short on 6/12
    // with tac scheduled would surface as a "shortage" even though
    // Nights isn't running that day.
    if (sh && !isShiftActiveOn(agency.data, sh, c.date)) return false;
    return c.assigned < c.minStaffing || c.oicAssigned < c.oicRequired;
  });

  // Coverage is a per-shift concern at a glance — a supervisor
  // skimming the dashboard wants "Days is short 2, Nights is short 1"
  // not an undifferentiated list. Bucket by shift (within
  // department for correctness), coloured by shift.color so each
  // block is instantly identifiable.
  const understaffedByShift = useMemo(() => {
    const shiftById = new Map(visibleShifts.map((s) => [s.id, s]));
    const deptById = new Map((departments.data ?? []).map((d) => [d.id, d]));
    const groups = new Map<string, {
      shiftId: string;
      shiftName: string;
      shiftColor: string;
      shiftSort: number;
      deptName: string;
      deptSort: number;
      items: typeof understaffed;
    }>();
    for (const c of understaffed) {
      const sh = shiftById.get(c.shiftId);
      if (!sh) continue;
      const dept = sh.departmentId ? deptById.get(sh.departmentId) : undefined;
      const g = groups.get(sh.id) ?? {
        shiftId: sh.id,
        shiftName: sh.name,
        shiftColor: sh.color ?? "#64748b",
        shiftSort: sh.sortOrder ?? 999,
        deptName: dept?.name ?? "Unassigned",
        deptSort: dept?.sortOrder ?? 999,
        items: [] as typeof understaffed,
      };
      g.items.push(c);
      groups.set(sh.id, g);
    }
    return [...groups.values()].sort((a, b) =>
      a.deptSort - b.deptSort
      || a.shiftSort - b.shiftSort
      || a.shiftName.localeCompare(b.shiftName),
    );
  }, [understaffed, visibleShifts, departments.data]);
  // Dept-scope the time-off / training pending lists by filtering
  // through this dept's employee ids. A pending request submitted
  // by a Patrol officer only counts on the Patrol dashboard.
  const deptEmpIds = useMemo(
    () => new Set((employees.data ?? []).map((e) => e.id)),
    [employees.data],
  );
  const pendingCount = (pendingTimeOff.data ?? [])
    .filter((t) => t.status === "requested" && deptEmpIds.has(t.employeeId))
    .length;

  // Owner / L1 inbox: training requests that an L2 partial-
  // approved and are waiting on a final approver. Empty (and
  // therefore renders nothing below) for everyone else. Scoped to
  // the active department so each dept sees its own queue.
  const trainingAwaitingFinal = useMemo(() => {
    const isFinalApprover = me?.level === "owner" || me?.level === "l1";
    if (!isFinalApprover) return [];
    return (pendingTimeOff.data ?? [])
      .filter((t) =>
        t.type === "training"
        && t.status === "l2_approved"
        && deptEmpIds.has(t.employeeId))
      .slice()
      .sort((a, b) => a.startDate.localeCompare(b.startDate));
  }, [pendingTimeOff.data, me?.level, deptEmpIds]);
  const empById = useMemo(
    () => new Map((employees.data ?? []).map((e) => [e.id, e])),
    [employees.data],
  );

  // "On schedule today" counts only assignments on this department's
  // currently-visible shifts. Without this filter the KPI would mix
  // Patrol + Dispatch + Animal Control numbers and a Patrol-only
  // dashboard would look wildly overstaffed.
  const onScheduleToday = useMemo(
    () => (todayAssignments.data ?? [])
      .filter((a) => visibleShiftIds.has(a.shiftId))
      .length,
    [todayAssignments.data, visibleShiftIds],
  );

  const mineUpcoming = useMemo(() => {
    if (!me?.employeeId) return [] as Array<{ date: string; shiftName: string; shiftColor: string }>;
    const shiftById = new Map((shifts.data ?? []).map((s) => [s.id, s]));
    // Skip rows whose note is a known off-code (XXX, PL, S, V, …)
    // so "My next two weeks" only lists days you're actually
    // working. Custom hour notes (e.g. "9-21") pass through.
    const codeMap = new Map((shiftCodes.data ?? []).map((c) => [c.code.toUpperCase(), c]));
    return (myAssignments.data ?? [])
      .filter((a) => {
        if (a.employeeId !== me.employeeId) return false;
        const note = (a.note ?? "").trim().toUpperCase();
        if (!note) return true;
        const c = codeMap.get(note);
        if (c) return c.coverageValue > 0;
        // All-letters custom note not in the code table = treat as
        // off (matches the coverage helper's convention).
        return !/^[A-Z]+$/.test(note);
      })
      .slice()
      .sort((a, b) => a.date.localeCompare(b.date))
      .slice(0, 6)
      .map((a) => {
        const s = shiftById.get(a.shiftId);
        return {
          date: a.date,
          shiftName: s?.name ?? a.shiftId,
          shiftColor: s?.color ?? "#64748b",
        };
      });
  }, [me?.employeeId, myAssignments.data, shifts.data, shiftCodes.data]);

  const mineTimeOff = useMemo(() => {
    if (!me?.employeeId) return [];
    return (myTimeOff.data ?? [])
      .filter((t) => t.employeeId === me.employeeId)
      .slice()
      .sort((a, b) => a.startDate.localeCompare(b.startDate));
  }, [me?.employeeId, myTimeOff.data]);

  return (
    <div className="p-4 md:p-6 space-y-4 md:space-y-6">
      <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-2">
        <div>
          <h1 className="text-xl font-semibold text-slate-900">
            {activeDept ? `${activeDept.name} dashboard` : "Dashboard"}
          </h1>
          <p className="text-sm text-slate-500">{new Date().toDateString()}</p>
        </div>
        {(departments.data ?? []).length > 1 && (
          <label className="flex items-center gap-2 text-xs text-slate-500 self-start md:self-auto">
            <span className="uppercase tracking-wider font-semibold">Department</span>
            <select
              className="rounded border border-slate-200 bg-white px-2 py-1 text-sm text-slate-800"
              value={resolvedDeptId ?? ""}
              onChange={(e) => {
                const v = e.target.value || null;
                if (v) {
                  try { localStorage.setItem(DEPT_STORAGE_KEY, v); } catch { /* ignore */ }
                }
                setActiveDeptId(v);
              }}
            >
              {(departments.data ?? []).map((d) => (
                <option key={d.id} value={d.id}>{d.name}</option>
              ))}
            </select>
          </label>
        )}
      </header>

      {me?.employeeId && (
        <div className="bg-white border border-slate-200 rounded-lg p-4">
          <h3 className="font-medium text-slate-900 mb-3">My next two weeks</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm">
            <div>
              <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-2">Upcoming shifts</div>
              {mineUpcoming.length === 0 ? (
                <p className="text-slate-500">No assignments scheduled.</p>
              ) : (
                <ul className="divide-y divide-slate-100">
                  {mineUpcoming.map((u) => (
                    <li key={u.date + u.shiftName} className="py-1.5 flex justify-between">
                      <span>{u.date}</span>
                      <span style={{ color: u.shiftColor }} className="font-medium">● {u.shiftName}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-2">My time off</div>
              {mineTimeOff.length === 0 ? (
                <p className="text-slate-500">No requests in this window.</p>
              ) : (
                <ul className="divide-y divide-slate-100">
                  {mineTimeOff.map((t) => (
                    <li key={t.id} className="py-1.5 flex justify-between">
                      <span>{t.startDate} → {t.endDate} · <span className="capitalize text-slate-500">{t.type}</span></span>
                      <span className={`capitalize font-medium ${
                        t.status === "approved" ? "text-green-700" :
                        t.status === "denied" ? "text-red-700" : "text-amber-700"
                      }`}>{t.status}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi
          label="Employees"
          value={employees.data?.length ?? "—"}
          hint={activeDept?.name}
        />
        <Kpi
          label={tacModeActive ? "12-hour + exempt shifts" : "Shifts configured"}
          value={visibleShifts.length || "—"}
          hint={activeDept?.name}
        />
        <Kpi label="On schedule today" value={onScheduleToday} />
        <Kpi label="Pending time off (7d)" value={pendingCount} />
      </div>

      {trainingAwaitingFinal.length > 0 && (
        <div className="bg-sky-50 border border-sky-200 rounded-lg p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-medium text-sky-900">
              Training awaiting your final approval
              <span className="ml-2 text-sky-600 font-normal">
                ({trainingAwaitingFinal.length})
              </span>
            </h3>
            <a href="#/training" className="text-xs font-medium text-sky-700 hover:underline">
              Open Training →
            </a>
          </div>
          <ul className="divide-y divide-sky-100">
            {trainingAwaitingFinal.slice(0, 5).map((t) => {
              const emp = empById.get(t.employeeId);
              return (
                <li key={t.id} className="py-1.5 flex justify-between gap-3 text-sm">
                  <span className="font-medium text-slate-900 truncate">
                    {emp ? `${emp.lastName}, ${emp.firstName}` : t.employeeId}
                  </span>
                  <span className="text-slate-600 whitespace-nowrap">
                    {t.startDate} → {t.endDate}
                  </span>
                </li>
              );
            })}
            {trainingAwaitingFinal.length > 5 && (
              <li className="pt-1.5 text-xs text-slate-500 italic">
                +{trainingAwaitingFinal.length - 5} more on the Training page
              </li>
            )}
          </ul>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white border border-slate-200 rounded-lg p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-medium text-slate-900">Understaffed shifts</h3>
            <div className="inline-flex rounded-md border border-slate-200 bg-slate-50 p-0.5 text-xs">
              {([
                { v: "7d", label: "7 days" },
                { v: "2w", label: "2 weeks" },
                { v: "month", label: "Month" },
              ] as const).map((opt) => (
                <button
                  key={opt.v}
                  type="button"
                  onClick={() => setUnderstaffedRange(opt.v)}
                  className={
                    "px-2 py-0.5 rounded font-medium transition " +
                    (understaffedRange === opt.v
                      ? "bg-white shadow-sm text-slate-900 border border-slate-200"
                      : "text-slate-500 hover:text-slate-700")
                  }
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
          {understaffed.length === 0 ? (
            <p className="text-sm text-slate-500">All shifts meet minimum staffing.</p>
          ) : (
            <div className="space-y-5 text-sm">
              {understaffedByShift.map((g) => (
                <div
                  key={g.shiftId}
                  className="rounded border border-slate-200 overflow-hidden"
                  style={{ borderLeft: `4px solid ${g.shiftColor}` }}
                >
                  <div
                    className="px-3 py-1.5 flex items-center justify-between text-[11px] uppercase tracking-wider"
                    style={{
                      backgroundColor: g.shiftColor,
                      color: readableTextOn(g.shiftColor),
                    }}
                  >
                    <span className="font-semibold">
                      {g.shiftName}
                      <span className="ml-2 normal-case tracking-normal"
                            style={{
                              color: readableTextOn(g.shiftColor) === "#ffffff"
                                ? "rgba(255,255,255,0.78)"
                                : "rgba(15,23,42,0.65)",
                            }}>
                        · {g.deptName}
                      </span>
                    </span>
                    <span style={{
                      color: readableTextOn(g.shiftColor) === "#ffffff"
                        ? "rgba(255,255,255,0.78)"
                        : "rgba(15,23,42,0.65)",
                    }}>{g.items.length} short</span>
                  </div>
                  <ul className="divide-y divide-slate-100">
                    {g.items.map((c) => {
                      const totalShort = c.assigned < c.minStaffing;
                      const oicShort = c.oicAssigned < c.oicRequired;
                      const body = (
                        <>
                          <span>{c.date}</span>
                          <span className="text-amber-700 font-medium whitespace-nowrap">
                            {totalShort && (
                              <span>{c.assigned} / {c.minStaffing} staff</span>
                            )}
                            {totalShort && oicShort && <span className="mx-1">·</span>}
                            {oicShort && (
                              <span>{c.oicAssigned} / {c.oicRequired} OIC</span>
                            )}
                          </span>
                        </>
                      );
                      if (!mayFix) {
                        return (
                          <li key={c.date + c.shiftId} className="py-2 px-3 flex justify-between gap-3">
                            {body}
                          </li>
                        );
                      }
                      return (
                        <li key={c.date + c.shiftId} className="py-0">
                          <button
                            type="button"
                            className="w-full py-2 px-3 flex justify-between gap-3 hover:bg-slate-50 text-left"
                            onClick={() => setFixCoverage({ shiftId: c.shiftId, date: c.date })}
                            title="Click to fix coverage"
                          >
                            {body}
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="bg-white border border-slate-200 rounded-lg p-4">
          <h3 className="font-medium text-slate-900 mb-3">Recent activity</h3>
          {(audit.data ?? []).length === 0 ? (
            <p className="text-sm text-slate-500">No activity yet.</p>
          ) : (
            <ul className="text-sm divide-y divide-slate-100">
              {audit.data!.map((a) => {
                const by = a.userId
                  ? (userLabel.get(a.userId) ?? `${a.userId.slice(0, 8)}…`)
                  : null;
                return (
                  <li key={a.id} className="py-2 flex justify-between gap-3">
                    <span className="min-w-0 truncate">
                      <span className="font-medium">{a.action}</span>
                      {" · "}{a.entityType}
                      {by && (
                        <span className="text-slate-500">{" · "}by {by}</span>
                      )}
                    </span>
                    <span className="text-slate-400 whitespace-nowrap">{new Date(a.createdAt).toLocaleString()}</span>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>

      {fixCoverage && (() => {
        const sh = visibleShifts.find((s) => s.id === fixCoverage.shiftId);
        if (!sh) return null;
        return (
          <FixCoverageModal
            shift={sh}
            date={fixCoverage.date}
            onClose={() => setFixCoverage(null)}
          />
        );
      })()}
    </div>
  );
}
